import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import LoginPasswordWidget from '../components/LoginPasswordWidget';
import TOTPWidget from '../components/TOTPWidget';
import { Lock, Copy, Trash, CheckCircle, ArrowRight, EnvelopeSimple, FileXls } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';

/* ─────────────────────────────────────────────────────────────
 *  Amazon Balance Checker — 3 screens in one router entry:
 *  • /amazon-balance           → Staff checker workspace
 *  • /amazon-balance/accounts  → Admin accounts manager
 *  • /amazon-balance/audit     → Admin full audit log
 *
 *  Intentionally separated from platform_accounts (per user ask
 *  Feb 2026): "pura seperate chahiye ye hame".
 * ───────────────────────────────────────────────────────────── */

const ALLOWED = ['admin', 'owner', 'staff', 'sub_staff', 'assistant'];
const STATUS_CHOICES = [
  { v: 'active', label: '✅ Active', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  { v: 'expired', label: '🔴 Expired', color: 'bg-red-100 text-red-800 border-red-300' },
  { v: 'password_incorrect', label: '❌ Password Incorrect', color: 'bg-orange-100 text-orange-800 border-orange-300' },
  { v: 'otp_not_received', label: '⚠️ OTP Not Received', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  { v: 'locked', label: '🔒 Locked', color: 'bg-rose-100 text-rose-800 border-rose-300' },
  { v: 'captcha_block', label: '🤖 Captcha Block', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  { v: 'other', label: '🔵 Other', color: 'bg-gray-100 text-gray-800 border-gray-300' },
];

function RoleGuard({ children, adminOnly = false }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'owner'].includes(user?.role);
  const allowed = ALLOWED.includes(user?.role);
  if (!allowed || (adminOnly && !isAdmin)) {
    return (
      <div className="max-w-md mx-auto mt-12 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
        <Lock size={32} className="mx-auto text-red-600 mb-2" weight="duotone" />
        <p className="font-bold text-red-800">Access Denied</p>
        <p className="text-sm text-red-700 mt-1">
          {adminOnly ? 'Admin/Owner only' : 'Role allowed nahi'}: {user?.role}
        </p>
      </div>
    );
  }
  return children;
}


/* ═══════════════════════ Staff Checker Workspace ═══════════════════════ */
export default function AmazonBalanceCheckerPage() {
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ status: '', balance_amount: '', gift_card_number: '', ticket_number: '', notes: '' });
  const [otpInfo, setOtpInfo] = useState(null);
  const [fetchingOtp, setFetchingOtp] = useState(false);

  const loadNext = useCallback(async () => {
    setLoading(true); setOtpInfo(null);
    setForm({ status: '', balance_amount: '', gift_card_number: '', ticket_number: '', notes: '' });
    try {
      const { data } = await api.get('/amazon-balance/accounts/next');
      setCurrent(data);
    } catch (err) {
      if (err.response?.status === 404) { setCurrent(null); toast.success('🎉 Sab accounts check ho gaye — aaj ke liye koi pending nahi'); }
      else toast.error(err.response?.data?.detail || 'Load fail');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadNext(); }, [loadNext]);

  const copy = (txt, label = 'Copied') => {
    if (!txt) return;
    navigator.clipboard.writeText(String(txt));
    toast.success(`${label}: ${txt}`);
  };

  const fetchOtp = async () => {
    if (!current?.otp_gmail) return toast.error('Gmail set nahi hai is account ke liye');
    setFetchingOtp(true);
    try {
      const { data } = await api.post('/amazon-balance/gmail/fetch-otp', {
        gmail: current.otp_gmail,
        amazon_email: current.email,
      });
      setOtpInfo(data);
      if (data.otp) { navigator.clipboard.writeText(data.otp); toast.success(`OTP: ${data.otp} (clipboard me copy ho gaya)`); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OTP fetch fail');
    } finally { setFetchingOtp(false); }
  };

  const submit = async () => {
    if (!form.status) return toast.error('Status choose karo');
    if (form.status === 'active' && !form.balance_amount) return toast.error('Active status ke liye balance amount zaroori');
    try {
      await api.post(`/amazon-balance/accounts/${current.id}/check`, {
        status: form.status,
        balance_amount: parseFloat(form.balance_amount || '0'),
        gift_card_number: form.gift_card_number.trim(),
        ticket_number: form.ticket_number.trim(),
        notes: form.notes.trim(),
      });
      toast.success('✅ Record saved — next account load ho raha hai');
      loadNext();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save fail'); }
  };

  const skip = async () => {
    if (!current) return;
    try { await api.post(`/amazon-balance/accounts/${current.id}/release`); } catch (e) { /* empty */ }
    loadNext();
  };

  return (
    <RoleGuard>
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🧾 Amazon Balance Checker</h1>
            <p className="text-sm text-gray-500 mt-0.5">1-by-1 account check karo · Gmail OTP auto-fetch · Sab record ho raha</p>
          </div>
          <div className="flex gap-2 text-xs">
            <a href="/amazon-balance/accounts" className="text-violet-600 hover:underline font-medium">⚙️ Manage accounts</a>
            <a href="/amazon-balance/audit" className="text-violet-600 hover:underline font-medium">📊 Audit log</a>
          </div>
        </div>

        {loading ? <div className="text-center py-12 text-gray-500">Loading…</div> :
         !current ? (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle size={36} weight="duotone" className="mx-auto text-emerald-500 mb-2" />
              <p className="font-bold text-gray-800">Sab accounts check ho gaye! 🎉</p>
              <p className="text-sm text-gray-500 mt-1">Recently checked accounts 6 hours baad dobara queue me aayenge.</p>
              <Button onClick={loadNext} className="mt-4" variant="outline">Check again</Button>
            </CardContent>
          </Card>
         ) : (
          <Card data-testid="ab-checker-card">
            <CardContent className="p-5 space-y-4">
              <div className="bg-gradient-to-r from-violet-600 to-fuchsia-600 -m-5 mb-3 p-4 text-white rounded-t-md">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-xs opacity-80">Current Account</div>
                    <div className="font-mono text-base font-bold break-all">{current.email}</div>
                    <div className="text-[11px] opacity-80 mt-1">Pehle {current.times_checked}x check ho chuka · {current.has_totp ? 'TOTP available' : `OTP via Gmail: ${current.otp_gmail || '(not set)'}`}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={skip} className="text-white border-white/40 hover:bg-white/10 bg-transparent" data-testid="ab-skip-btn">
                    Skip <ArrowRight size={14} className="ml-1" />
                  </Button>
                </div>
              </div>

              {/* Credentials block — click-to-copy */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button onClick={() => copy(current.email, 'Email')} className="flex items-center justify-between gap-2 p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded" data-testid="ab-copy-email">
                  <div className="text-left">
                    <div className="text-[10px] text-blue-700 font-bold uppercase">📧 Email</div>
                    <div className="font-mono text-sm text-blue-900 break-all">{current.email}</div>
                  </div>
                  <Copy size={14} className="text-blue-400 shrink-0" />
                </button>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded">
                  <div className="text-[10px] text-amber-700 font-bold uppercase">🔑 Password</div>
                  {current.password ? (
                    current.has_totp ? (
                      <LoginPasswordWidget password={current.password} secret={current.totp_secret} label="Pwd + OTP" compact testId="ab-login-pwd" />
                    ) : (
                      <button onClick={() => copy(current.password, 'Password')} className="text-sm font-mono text-amber-900 hover:underline" data-testid="ab-copy-pwd">
                        {current.password} 📋
                      </button>
                    )
                  ) : <span className="text-xs text-gray-500">—</span>}
                </div>
                {current.has_totp && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                    <div className="text-[10px] text-purple-700 font-bold uppercase">🔐 TOTP</div>
                    <TOTPWidget secret={current.totp_secret} label="TOTP" />
                  </div>
                )}
                {!current.has_totp && current.otp_gmail && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                    <div className="text-[10px] text-emerald-700 font-bold uppercase flex items-center gap-1">
                      <EnvelopeSimple size={11} /> Gmail OTP ({current.otp_gmail})
                    </div>
                    <Button size="sm" onClick={fetchOtp} disabled={fetchingOtp} className="mt-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="ab-fetch-otp">
                      {fetchingOtp ? 'Fetching…' : '📥 Fetch latest Amazon OTP'}
                    </Button>
                    {otpInfo?.otp && (
                      <button onClick={() => copy(otpInfo.otp, 'OTP')} className="mt-2 block text-sm font-mono bg-white border border-emerald-300 rounded px-2 py-1 hover:bg-emerald-50 w-full text-left" data-testid="ab-otp-result">
                        OTP: <span className="font-bold text-emerald-800">{otpInfo.otp}</span> 📋
                        <div className="text-[10px] text-gray-500 truncate">{otpInfo.subject}</div>
                      </button>
                    )}
                  </div>
                )}
              </div>

              <a href="https://www.amazon.in/ap/signin" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm bg-orange-50 border border-orange-200 text-orange-800 hover:bg-orange-100 rounded px-3 py-1.5" data-testid="ab-open-amazon">
                🔓 Open Amazon Login in new tab
              </a>

              {/* Result form */}
              <div className="border-t pt-4 mt-3 space-y-3">
                <h3 className="font-bold text-gray-800 text-sm">📝 Check result submit karein</h3>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Status *</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mt-1">
                    {STATUS_CHOICES.map(s => (
                      <button
                        key={s.v}
                        onClick={() => setForm({ ...form, status: s.v })}
                        className={`text-[11px] font-bold border rounded px-2 py-1.5 transition-all ${form.status === s.v ? s.color + ' ring-2 ring-offset-1 ring-violet-500' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'}`}
                        data-testid={`ab-status-${s.v}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-700">Balance (₹)</label>
                    <Input type="number" value={form.balance_amount} onChange={e => setForm({ ...form, balance_amount: e.target.value })} placeholder="e.g., 10000" className="h-9 text-sm mt-1" data-testid="ab-balance-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700">Gift Card Number (Amazon se)</label>
                    <Input value={form.gift_card_number} onChange={e => setForm({ ...form, gift_card_number: e.target.value })} placeholder="e.g., ABCD-EFGH-IJKL" className="h-9 text-sm mt-1" data-testid="ab-gc-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700">Ticket Number (Amazon CS)</label>
                    <Input value={form.ticket_number} onChange={e => setForm({ ...form, ticket_number: e.target.value })} placeholder="e.g., 1234567890" className="h-9 text-sm mt-1" data-testid="ab-ticket-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-700">Notes (optional)</label>
                    <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any extra info…" className="h-9 text-sm mt-1" data-testid="ab-notes-input" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={submit} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="ab-save-btn">
                    💾 Save & Next
                  </Button>
                  <Button variant="outline" onClick={skip} data-testid="ab-skip-btn-2">Skip</Button>
                </div>
              </div>
            </CardContent>
          </Card>
         )}
      </div>
    </RoleGuard>
  );
}


/* ═══════════════════════ Admin: Accounts Manager ═══════════════════════ */
export function AmazonBalanceAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showGmail, setShowGmail] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', password: '', totp_secret: '', otp_gmail: '', notes: '' });
  const [bulkText, setBulkText] = useState('');
  const [gmailList, setGmailList] = useState([]);
  const [gmailForm, setGmailForm] = useState({ gmail: '', app_password: '', label: '' });
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: accs }, { data: st }, { data: gs }] = await Promise.all([
        api.get('/amazon-balance/accounts', { params: { status: filter || undefined, limit: 1000 } }),
        api.get('/amazon-balance/stats'),
        api.get('/amazon-balance/gmail/credentials'),
      ]);
      setAccounts(accs || []); setStats(st || {}); setGmailList(gs || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Load fail'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const addOne = async () => {
    if (!addForm.email.includes('@')) return toast.error('Valid email required');
    try {
      await api.post('/amazon-balance/accounts', addForm);
      toast.success('Account added');
      setShowAdd(false); setAddForm({ email: '', password: '', totp_secret: '', otp_gmail: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Add fail'); }
  };

  const bulkAdd = async () => {
    if (!bulkText.trim()) return toast.error('Paste some data');
    try {
      const { data } = await api.post('/amazon-balance/accounts/bulk-paste', { text: bulkText });
      toast.success(`Added: ${data.added} · Skipped: ${data.skipped}`);
      setShowBulk(false); setBulkText(''); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk fail'); }
  };

  const uploadExcel = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append('file', f);
    try {
      const { data } = await api.post('/amazon-balance/accounts/upload-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Excel uploaded: added ${data.added}, skipped ${data.skipped}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Excel upload fail'); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };

  const saveGmail = async () => {
    if (!gmailForm.gmail.includes('@') || gmailForm.app_password.length < 8) return toast.error('Valid gmail + 8+ char app password');
    try {
      await api.post('/amazon-balance/gmail/credentials', gmailForm);
      toast.success('Gmail credential saved');
      setGmailForm({ gmail: '', app_password: '', label: '' }); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save fail'); }
  };

  const delGmail = async (g) => {
    if (!window.confirm(`Delete credential for ${g}?`)) return;
    try { await api.delete(`/amazon-balance/gmail/credentials/${encodeURIComponent(g)}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Delete fail'); }
  };

  const delAccount = async (id) => {
    if (!window.confirm('Delete this account?')) return;
    try { await api.delete(`/amazon-balance/accounts/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Delete fail'); }
  };

  return (
    <RoleGuard adminOnly>
      <div className="space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold">⚙️ Amazon Balance — Accounts Manager</h1>
            <p className="text-sm text-gray-500 mt-0.5">Accounts upload/manage + Gmail OTP credentials</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" onClick={() => setShowAdd(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="ab-add-btn">+ Add account</Button>
            <Button size="sm" variant="outline" onClick={() => setShowBulk(true)} data-testid="ab-bulk-btn">📋 Bulk paste</Button>
            <input type="file" ref={fileRef} accept=".xlsx,.xls" onChange={uploadExcel} className="hidden" id="ab-excel-input" />
            <label htmlFor="ab-excel-input" className="inline-flex items-center gap-1 h-9 px-3 border rounded-md bg-white hover:bg-gray-50 text-sm cursor-pointer" data-testid="ab-excel-upload">
              <FileXls size={14} /> Upload Excel
            </label>
            <Button size="sm" variant="outline" onClick={() => setShowGmail(true)} data-testid="ab-gmail-btn">📧 Gmail credentials ({gmailList.length})</Button>
            <a href="/amazon-balance" className="text-sm text-violet-600 hover:underline font-medium self-center">→ Staff workspace</a>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { k: 'Total', v: stats.total_accounts || 0, c: 'bg-gray-100 text-gray-800' },
            { k: 'Unchecked', v: stats.never_checked || 0, c: 'bg-amber-100 text-amber-800' },
            { k: '✅ Active', v: stats.active || 0, c: 'bg-emerald-100 text-emerald-800' },
            { k: '🔴 Expired', v: stats.expired || 0, c: 'bg-red-100 text-red-800' },
            { k: '❌ Bad', v: stats.bad || 0, c: 'bg-rose-100 text-rose-800' },
            { k: '💰 Active ₹', v: `₹${Math.round(stats.active_balance_total || 0).toLocaleString('en-IN')}`, c: 'bg-blue-100 text-blue-800' },
          ].map(s => (
            <div key={s.k} className={`p-3 rounded-lg ${s.c} text-center`}>
              <div className="text-[10px] font-bold uppercase">{s.k}</div>
              <div className="text-lg font-bold">{s.v}</div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 items-center flex-wrap">
          <label className="text-xs font-semibold text-gray-600">Filter:</label>
          {['', 'unchecked', 'active', 'expired', 'bad', 'checked'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`text-xs px-2.5 py-1 rounded border ${filter === f ? 'bg-violet-600 text-white border-violet-600' : 'bg-white hover:bg-gray-50 border-gray-300'}`} data-testid={`ab-filter-${f || 'all'}`}>
              {f || 'all'}
            </button>
          ))}
        </div>

        <div className="card-flat overflow-x-auto">
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Credentials</TableHead>
                <TableHead>OTP source</TableHead>
                <TableHead className="text-right">Checked×</TableHead>
                <TableHead>Last result</TableHead>
                <TableHead>Last check</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-6">Loading…</TableCell></TableRow> :
               accounts.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-6 text-gray-500">Koi account nahi · Upload karo</TableCell></TableRow> :
               accounts.map(a => (
                <TableRow key={a.id} data-testid={`ab-row-${a.id}`}>
                  <TableCell className="font-mono text-xs">{a.email}</TableCell>
                  <TableCell className="text-xs">
                    {a.has_password && <span className="inline-block bg-amber-100 text-amber-700 rounded px-1 mr-1">Pwd</span>}
                    {a.has_totp && <span className="inline-block bg-purple-100 text-purple-700 rounded px-1">TOTP</span>}
                    {!a.has_password && !a.has_totp && <span className="text-red-600">⚠️ missing</span>}
                  </TableCell>
                  <TableCell className="text-xs text-gray-700">{a.has_totp ? 'TOTP' : (a.otp_gmail || '—')}</TableCell>
                  <TableCell className="text-right font-bold">{a.times_checked}</TableCell>
                  <TableCell>
                    {a.last_check_status ? (
                      <>
                        <span className={`inline-block text-[10px] font-bold rounded px-1.5 py-0.5 border ${(STATUS_CHOICES.find(s => s.v === a.last_check_status) || {}).color || ''}`}>
                          {(STATUS_CHOICES.find(s => s.v === a.last_check_status) || {}).label || a.last_check_status}
                        </span>
                        {a.last_check_balance > 0 && <span className="block text-[11px] text-emerald-700 font-bold mt-0.5">₹{Math.round(a.last_check_balance).toLocaleString('en-IN')}</span>}
                      </>
                    ) : <span className="text-xs text-gray-400">never</span>}
                  </TableCell>
                  <TableCell className="text-xs text-gray-600">
                    {a.last_checked_at ? (<><div>{formatDateTime(a.last_checked_at)}</div><div className="text-[10px] text-gray-400">by {a.last_checked_by_name}</div></>) : '-'}
                  </TableCell>
                  <TableCell>
                    <button onClick={() => delAccount(a.id)} className="text-red-600 hover:bg-red-50 rounded p-1" title="Delete"><Trash size={12} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Add single */}
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="max-w-md" data-testid="ab-add-dialog">
            <DialogHeader><DialogTitle>Add Amazon Account</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {['email', 'password'].map(k => (
                <div key={k}><label className="text-xs font-semibold">{k} *</label><Input value={addForm[k]} onChange={e => setAddForm({ ...addForm, [k]: e.target.value })} className="h-9 text-sm mt-1" /></div>
              ))}
              <div><label className="text-xs font-semibold">TOTP secret (empty agar Gmail OTP use karna hai)</label><Input value={addForm.totp_secret} onChange={e => setAddForm({ ...addForm, totp_secret: e.target.value })} placeholder="Base32 secret" className="h-9 text-sm mt-1 font-mono" /></div>
              <div><label className="text-xs font-semibold">OTP Gmail (jab TOTP nahi hai)</label><Input value={addForm.otp_gmail} onChange={e => setAddForm({ ...addForm, otp_gmail: e.target.value })} placeholder="xyz@gmail.com" className="h-9 text-sm mt-1" /></div>
              <div><label className="text-xs font-semibold">Notes</label><Input value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} className="h-9 text-sm mt-1" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button><Button onClick={addOne}>Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk paste */}
        <Dialog open={showBulk} onOpenChange={setShowBulk}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Bulk Paste Accounts</DialogTitle></DialogHeader>
            <div className="text-xs text-gray-600 mb-2">Format per line: <code>email,password,totp_secret,otp_gmail</code> (comma OR tab)</div>
            <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} placeholder={`abc@gmail.com,MyPwd,JBSWY3DPEHPK3PXP,otpbox@gmail.com\nxyz@gmail.com,Other,,gmailotp@gmail.com`} className="w-full min-h-[260px] p-3 font-mono text-xs border rounded" data-testid="ab-bulk-text" />
            <DialogFooter><Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button><Button onClick={bulkAdd}>Import</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Gmail credentials */}
        <Dialog open={showGmail} onOpenChange={setShowGmail}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>📧 Gmail Credentials — App Passwords</DialogTitle></DialogHeader>
            <div className="bg-amber-50 border border-amber-300 rounded p-2 text-[11px] text-amber-900 mb-3">
              💡 Google Account → Security → 2FA enable → App passwords → generate 16-digit password. Usi ko yahan daalein (regular password NAHI).
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Input placeholder="gmail address" value={gmailForm.gmail} onChange={e => setGmailForm({ ...gmailForm, gmail: e.target.value })} className="h-9 text-sm" />
              <Input placeholder="app password (16 chars)" value={gmailForm.app_password} onChange={e => setGmailForm({ ...gmailForm, app_password: e.target.value })} className="h-9 text-sm font-mono" />
              <Input placeholder="label (optional)" value={gmailForm.label} onChange={e => setGmailForm({ ...gmailForm, label: e.target.value })} className="h-9 text-sm" />
            </div>
            <Button size="sm" onClick={saveGmail} className="bg-violet-600 hover:bg-violet-700">Save credential</Button>
            <div className="mt-4 border-t pt-3 max-h-60 overflow-y-auto">
              {gmailList.length === 0 ? <div className="text-xs text-gray-400 text-center py-3">Koi Gmail credential saved nahi</div> :
               gmailList.map(g => (
                <div key={g.gmail} className="flex items-center justify-between text-sm py-1 px-2 hover:bg-gray-50 rounded">
                  <div><div className="font-mono text-xs">{g.gmail}</div>{g.label && <div className="text-[10px] text-gray-500">{g.label}</div>}</div>
                  <button onClick={() => delGmail(g.gmail)} className="text-red-600 p-1"><Trash size={12} /></button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </RoleGuard>
  );
}


/* ═══════════════════════ Admin: Audit Log ═══════════════════════ */
export function AmazonBalanceAuditPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', status: 'all', email_q: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.email_q) params.email_q = filters.email_q;
      const { data } = await api.get('/amazon-balance/logs', { params });
      setLogs(data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Load fail'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <RoleGuard adminOnly>
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold">📊 Amazon Balance — Audit Log</h1>
            <p className="text-sm text-gray-500 mt-0.5">Kis staff ne kab kaunsa account check kiya — full record</p>
          </div>
          <div className="flex gap-2 text-xs">
            <a href="/amazon-balance" className="text-violet-600 hover:underline">→ Staff workspace</a>
            <a href="/amazon-balance/accounts" className="text-violet-600 hover:underline">→ Accounts manager</a>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap items-end">
          <div><label className="text-[10px] font-semibold text-gray-600">From</label><Input type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} className="h-9 text-xs" /></div>
          <div><label className="text-[10px] font-semibold text-gray-600">To</label><Input type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} className="h-9 text-xs" /></div>
          <div><label className="text-[10px] font-semibold text-gray-600">Status</label>
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className="h-9 text-xs rounded-md border border-gray-300 px-2 bg-white">
              <option value="all">all</option>
              {STATUS_CHOICES.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
          <div><label className="text-[10px] font-semibold text-gray-600">Email search</label><Input value={filters.email_q} onChange={e => setFilters({ ...filters, email_q: e.target.value })} placeholder="xyz@gmail" className="h-9 text-xs" /></div>
          <Button size="sm" onClick={load} disabled={loading} className="bg-violet-600 hover:bg-violet-700">{loading ? '…' : 'Refresh'}</Button>
        </div>

        <div className="card-flat overflow-x-auto">
          <Table className="table-dense">
            <TableHeader><TableRow>
              <TableHead>Checked At</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Gift Card #</TableHead>
              <TableHead>Ticket #</TableHead>
              <TableHead>By</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={8} className="text-center py-6">Loading…</TableCell></TableRow> :
               logs.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-6 text-gray-500">No records</TableCell></TableRow> :
               logs.map(l => (
                <TableRow key={l.id} data-testid={`ab-log-${l.id}`}>
                  <TableCell className="text-xs">{formatDateTime(l.checked_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{l.account_email}</TableCell>
                  <TableCell><span className={`text-[10px] font-bold rounded px-1.5 py-0.5 border ${(STATUS_CHOICES.find(s => s.v === l.status) || {}).color || ''}`}>{(STATUS_CHOICES.find(s => s.v === l.status) || {}).label || l.status}</span></TableCell>
                  <TableCell className="text-right text-sm">{l.balance_amount > 0 ? <span className="font-bold text-emerald-700">₹{Math.round(l.balance_amount).toLocaleString('en-IN')}</span> : '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{l.gift_card_number ? <span onClick={() => { navigator.clipboard.writeText(l.gift_card_number); toast.success('Copied'); }} className="cursor-pointer hover:underline">{l.gift_card_number}</span> : '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{l.ticket_number || '-'}</TableCell>
                  <TableCell className="text-xs">{l.checked_by_name}</TableCell>
                  <TableCell className="text-xs text-gray-600 max-w-[200px] truncate" title={l.notes}>{l.notes || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </RoleGuard>
  );
}
